<template>
    <div class="identity-verification-page guided-steps-page inner p-15px">
        <h1 class="identity-verification-page__title">本人確認</h1>
        <div class="member-edit" v-if="profileLoaded">
        <div class="identity-verification-notice" v-if="fields.identifierEditable">
            <p class="identity-verification-notice__title">ログイン方法変更のお知らせ</p>
            <p class="identity-verification-notice__text">
                このたび、サービスの利便性向上のため、ログイン方法を変更いたしました。<br>
                今後は、メールアドレスまたは電話番号をログインIDとしてご利用いただきます。<br>
                <strong>これまでメールアドレスや電話番号以外のIDでログインされていたお客様</strong>は、本人確認の際に、新しいログインIDのご登録をお願いいたします。
            </p>
            <p class="identity-verification-notice__text identity-verification-notice__password" v-if="mode === 'PASSWORD_UPGRADE'">
                また、セキュリティ強化に伴い、これまでのパスワードはご利用いただけなくなります。お手数ですが、英小文字と数字を含む8文字以上の新しいパスワードを設定してください。
            </p>
            <p class="identity-verification-notice__text">
                ご不便をおかけいたしますが、ご理解とご協力をお願いいたします。
            </p>
        </div>
        <el-form class="form mt2" ref="memberIdentityVerification" :model="form" :rules="rules">
            <!-- Guided steps (client design): ① everything except the code, ending with the send
                 button; ② the code + 確定. The server flow is unchanged (send code, then submit
                 everything to /member/identity-verification/verify). Step ② is dimmed AND locked
                 until a code is sent in this page visit, so it behaves exactly as it looks; the
                 trade-off is that a member who reloads after receiving a code must send a new one. -->
            <div class="iv-steps">
                <section :class="['iv-step', codeSent ? 'is-done' : 'is-active']">
                    <div class="iv-step__marker">
                        <span class="iv-step__number">
                            <i v-if="codeSent" class="el-icon-check" aria-hidden="true"></i>
                            <template v-else>1</template>
                        </span>
                    </div>
                    <div class="iv-step__body">
                        <h2 class="iv-step__title">ログイン情報を入力</h2>
                        <table class="table-register">
                            <tr v-if="!fields.identifierEditable">
                              <th id="login-id" class="column-label">ログインID</th>
                              <td class="column-input max-width-400">
                                  <el-input class="input" :value="loginId" disabled></el-input>
                              </td>
                            </tr>
                            <tr v-if="fields.identifierEditable">
                              <th id="new-login-id" class="column-label required">新しいログインID<span class="column-label__sub">（電話番号またはメールアドレス）</span></th>
                              <td class="column-input max-width-400">
                                  <el-form-item prop="newLoginId">
                                      <el-input class="input" v-model="form.newLoginId" :disabled="disabled" placeholder="メールアドレスまたは電話番号"></el-input>
                                      <span class="d-inline-block small-text">今後のログインに使用する、メールアドレスまたは電話番号のいずれかを入力してください</span>
                                  </el-form-item>
                              </td>
                            </tr>
                            <tr v-if="fields.showCurrentPassword">
                              <th id="old-password" class="column-label required">現在のパスワード</th>
                              <td class="column-input max-width-400">
                                  <el-form-item prop="currentPassword">
                                      <el-input type="password" class="input" v-model="form.currentPassword" :disabled="disabled"></el-input>
                                  </el-form-item>
                              </td>
                            </tr>
                            <tr v-if="fields.showNewPassword">
                              <th id="new-password" class="column-label required">新しいパスワード</th>
                              <td class="column-input max-width-400">
                                  <el-form-item prop="newPassword">
                                      <el-input type="password" class="input" v-model="form.newPassword" :disabled="disabled"></el-input>
                                      <span class="d-inline-block small-text">小文字と数字を含めて8～32文字で組み合わせて設定してください</span>
                                  </el-form-item>
                              </td>
                            </tr>
                            <tr v-if="fields.showNewPasswordConfirm">
                              <th id="password-confirm" class="column-label required">新しいパスワード（確認）</th>
                              <td class="column-input max-width-400">
                                  <el-form-item prop="newPasswordConfirm">
                                      <el-input type="password" class="input" v-model="form.newPasswordConfirm" :disabled="disabled"></el-input>
                                  </el-form-item>
                              </td>
                            </tr>
                        </table>
                        <div class="iv-step__action">
                            <el-button type="primary" class="send-code-button" @click="handleSendVerificationCode" :disabled="disabled || verificationCodeCooldown > 0">本人確認コードを送信</el-button>
                            <span v-if="verificationCodeCooldown > 0" class="d-inline-block small-text">再送信可能まで30秒</span>
                        </div>
                    </div>
                </section>
                <section :class="['iv-step', codeSent ? 'is-active' : 'is-pending']">
                    <div class="iv-step__marker">
                        <span class="iv-step__number">2</span>
                    </div>
                    <div class="iv-step__body">
                        <h2 class="iv-step__title">本人確認コードを入力</h2>
                        <table class="table-register">
                            <tr v-if="fields.showOtc">
                              <th id="verification-code" class="column-label required">本人確認コード</th>
                              <td class="column-input max-width-400">
                                  <el-form-item prop="verificationCode">
                                      <el-input class="input verification-code-input" v-model="form.verificationCode" :disabled="stepTwoDisabled" placeholder="本人確認コード"></el-input>
                                      <span v-if="!codeSent" class="d-inline-block small-text">先に本人確認コードを送信してください</span>
                                  </el-form-item>
                              </td>
                            </tr>
                        </table>
                        <div class="iv-step__action text-center">
                            <el-button type="primary" @click="handleIdentityVerification" :disabled="stepTwoDisabled">確定</el-button>
                        </div>
                    </div>
                </section>
            </div>
        </el-form>
        </div>
    </div>
</template>

<script>
    import MemberClientApi from '@/api/member/member-client.js'
    import JwtService from '@/common/jwt.service'
    import { applySessionFromResponse } from '@/common/authSession'
    import { SESSION_FLAG } from '@/router/routeGuards'
    import { VERIFICATION_MODE, getVisibleFields } from '@/components/identityVerification/fieldVisibility'
    import { looksLikeEmailOrPhone } from '@/components/identityVerification/loginIdShape'
    import verificationCodeCooldownMixin from '@/mixins/verificationCodeCooldownMixin'

    // Mode-aware across all three identity-verification modes - Standard (read-only login_id,
    // current password + OTC only), Password Upgrade (editable new identifier + new
    // password/confirm + current password + OTC), and Credential Migration (editable new
    // identifier + current password + OTC, no new password). Which fields render is decided by
    // fieldVisibility.getVisibleFields; the mode itself is computed client-side purely to decide
    // rendering - the backend independently re-derives and enforces the real mode regardless.
    export default {
      name: 'memberIdentityVerification',
      mixins: [verificationCodeCooldownMixin],
      data () {
            const newLoginIdIsEmailOrPhoneShaped = (rule, value, callback) => {
                // required:true (below) already covers the blank case - this catches a non-blank
                // value that isn't email/phone-shaped before it ever reaches the backend.
                if (!value || looksLikeEmailOrPhone(value)) {
                    callback()
                } else {
                    callback(new Error('メールアドレスまたは電話番号の形式で入力してください'))
                }
            }
            const newPasswordDiffersFromCurrent = (rule, value, callback) => {
                if (value && this.form.currentPassword === this.form.newPassword) {
                    callback(new Error('新しいパスワードに現在のパスワードは設定できません'))
                } else {
                    callback()
                }
            }
            const newPasswordConfirmMatches = (rule, value, callback) => {
                if (this.fields.showNewPasswordConfirm) {
                    if (!value) {
                        callback(new Error('新しいパスワード（確認）を入力してください'))
                    } else if (value !== this.form.newPassword) {
                        callback(new Error('新しいパスワード（確認）と新しいパスワードが一致しません'))
                    } else {
                        callback()
                    }
                } else {
                    callback()
                }
            }
            return {
                profileLoaded: false,
                cognitoPasswordUpgradeRequired: false,
                form: {
                    newLoginId: '',
                    currentPassword: '',
                    newPassword: '',
                    newPasswordConfirm: '',
                    verificationCode: ''
                },
                rules: {
                    newLoginId: [
                      { required: true, message: '新しいログインIDを入力してください', trigger: 'blur' },
                      { validator: newLoginIdIsEmailOrPhoneShaped, trigger: 'blur' }
                    ],
                    currentPassword: [
                      { required: true, message: '現在のパスワードを入力してください', trigger: 'blur' }
                    ],
                    // PasswordPolicy.kt: 8+ chars, lowercase+digit required, "¥" forbidden.
                    newPassword: [
                      { required: true,
                        pattern: /^(?=.*[a-z])(?=.*\d)[^¥]{8,32}$/,
                        message: '小文字と数字を含めて8文字以上で入力してください',
                        trigger: 'blur'
                      },
                      { validator: newPasswordDiffersFromCurrent, trigger: 'blur' }
                    ],
                    newPasswordConfirm: [
                      { validator: newPasswordConfirmMatches, trigger: 'blur' }
                    ],
                    verificationCode: [
                      { required: true, message: '本人確認コードを入力してください', trigger: 'blur' }
                    ]
                },
                disabled: false,
                // Set once a code has actually been sent in this page visit; drives the step
                // pointers and unlocks step ②.
                codeSent: false,
                postVerificationRouteName: 'portalMember'
            }
        },
        computed: {
            // Parsed directly from the JWT payload, never a data-service call - GET /member/
            // selfInfo is not on the quarantine jail's endpoint whitelist. Only used for
            // Standard Mode's read-only display; migration modes show an editable field instead
            // (form.newLoginId).
            loginId () {
                return JwtService.getLoginId()
            },
            mode () {
                const sessionFlag = JwtService.getSessionFlag()
                if (sessionFlag === SESSION_FLAG.MIGRATION_REQUIRED) {
                    return this.cognitoPasswordUpgradeRequired
                        ? VERIFICATION_MODE.PASSWORD_UPGRADE
                        : VERIFICATION_MODE.CREDENTIAL_MIGRATION
                }
                return VERIFICATION_MODE.STANDARD
            },
            fields () {
                return getVisibleFields(this.mode)
            },
            stepTwoDisabled () {
                return this.disabled || !this.codeSent
            }
        },
        created () {
            // cognitoPasswordUpgradeRequired isn't in the JWT - it's the one extra fact needed to
            // distinguish Password Upgrade from Credential Migration within a shared
            // MIGRATION_REQUIRED session. A fetch failure defaults to Credential Migration mode
            // (no new-password fields) rather than blocking the screen.
            MemberClientApi.getLoginUserInfo().then(member => {
                this.cognitoPasswordUpgradeRequired = !!member.cognitoPasswordUpgradeRequired
            }).catch(() => {
                this.cognitoPasswordUpgradeRequired = false
            }).finally(() => {
                this.profileLoaded = true
            })
        },
        methods: {
            handleSendVerificationCode () {
                if (this.verificationCodeCooldown > 0) {
                    this.$message({
                        message: '本人確認コードは30秒に1回のみ送信できます',
                        type: 'error',
                        duration: 3000
                    })
                    return
                }
                // Step ① must be complete before a code is sent (client request): run the form's
                // own rules for just step ①'s fields, so errors show inline under each field.
                return this.validateStepOne().then(valid => {
                    if (!valid) {
                        return
                    }
                    return this.sendVerificationCode()
                })
            },
            validateStepOne () {
                const props = []
                if (this.fields.identifierEditable) props.push('newLoginId')
                if (this.fields.showCurrentPassword) props.push('currentPassword')
                if (this.fields.showNewPassword) props.push('newPassword')
                if (this.fields.showNewPasswordConfirm) props.push('newPasswordConfirm')
                const form = this.$refs['memberIdentityVerification']
                return Promise.all(props.map(prop => new Promise(resolve => {
                    form.validateField(prop, errorMessage => resolve(!errorMessage))
                }))).then(results => results.every(Boolean))
            },
            sendVerificationCode () {
                // purpose is always REGISTRATION - Standard Mode resolves the destination
                // server-side from the JWT subject; migration modes send to the new identifier
                // explicitly.
                const payload = { purpose: 'REGISTRATION' }
                if (this.fields.identifierEditable) {
                    payload.targetIdentifier = this.form.newLoginId
                }
                return MemberClientApi.sendOtc(payload).then(() => {
                    this.codeSent = true
                    this.$message({
                        message: 'お客様に本人確認コードを送信しました',
                        type: 'success',
                        duration: 3000
                    })
                    this.startVerificationCodeCooldown()
                }).catch(err => {
                    this.alertMessage(err.response.data.message, 'エラー')
                })
            },
            handleIdentityVerification () {
                this.$refs['memberIdentityVerification'].validate((valid) => {
                    if (valid) {
                        this.disabled = true
                        const payload = {
                            currentPassword: this.form.currentPassword,
                            verificationCode: this.form.verificationCode
                        }
                        if (this.fields.identifierEditable) {
                            payload.newLoginId = this.form.newLoginId
                        }
                        if (this.fields.showNewPassword) {
                            payload.newPassword = this.form.newPassword
                        }
                        MemberClientApi.verifyIdentity(payload).then(response => {
                            // Waits for the real profile fetch (triggered inside
                            // applySessionFromResponse) before moving on.
                            return applySessionFromResponse(this.$store, response).then(() => {
                                // Route by the new session's own flag - sending either outcome to
                                // the other's destination gets blocked by the routing guard.
                                this.postVerificationRouteName = JwtService.getSessionFlag() === SESSION_FLAG.TERMS_ACKNOWLEDGEMENT_REQUIRED
                                    ? 'acceptTerms'
                                    : 'portalMember'
                                return this.$alert(
                                    '相続のせんせいはAIユイゴン&nbsp;Well-Bと連動しておりますので<br>AIユイゴン&nbsp;Well-Bでも同じログイン情報でのご利用が可能でございます',
                                    '本人確認が完了しました',
                                    { confirmButtonText: this.$t('commons.ok'), dangerouslyUseHTMLString: true, center: true, customClass: 'identity-verified-alert' }
                                ).then(() => {
                                    this.$router.push({ name: this.postVerificationRouteName })
                                })
                            })
                        }).catch(err => {
                            this.disabled = false
                            // Either way the old code is no longer usable - wrong is still wrong,
                            // right has already been consumed.
                            this.form.verificationCode = ''
                            this.alertMessage(err.response.data.message, '入力情報エラー')
                        })
                    } else {
                        this.alertMessage('入力情報を再度ご確認ください。', '入力情報エラー')
                    }
                })
            },
            alertMessage (message, title) {
              this.$alert(message, title, {
                  confirmButtonText: this.$t('commons.ok')
              })
            }
        }
    }
</script>

<style>
    @import 'member_register.css';
    @import '../../../../assets/css/guided_steps.css';
    /* PC only: the default 420px box wraps each line of the completion message mid-sentence
       (e.g. 「…連動しております / ので」). Wide enough for both lines to fit whole. The box is
       mounted on <body>, so this has to live in this unscoped block. */
    @media screen and (min-width: 769px) {
        .el-message-box.identity-verified-alert {
            width: 560px;
        }
    }
    .identity-verification-page {
        padding-bottom: 3em;
    }
    .identity-verification-page__title {
        margin: 0 0 1.25em;
        text-align: center;
        font-size: 28px;
        font-weight: bold;
        color: #333;
    }
    .member-edit {
        padding-bottom: 2em;
    }
    .identity-verification-notice {
        background: #fffdf7;
        border: 1px solid #c0b283;
        padding: 1em 1.25em;
        margin-bottom: 1em;
        color: #333;
        font-size: 14px;
        line-height: 1.7;
    }
    .identity-verification-notice__title {
        margin: 0 0 0.5em;
        font-size: 16px;
        font-weight: bold;
        color: #5c4a1a;
    }
    .identity-verification-notice__text {
        margin: 0;
    }
    .identity-verification-notice__text + .identity-verification-notice__text {
        margin-top: 0.75em;
    }
    @media screen and (min-width:769px) {
        .form th {
            width: 30%;
        }
    }
</style>
